import { useCallback, useContext, useRef, useState } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { useApiContextFetch, TableContext } from "@swiggy-private/portal-dineout-context";
import { triggerQuickAction } from "../api/quickActions";
import { SUCCESS_STATUS_CODE } from "../constants/api";

export interface QuickActionHalfRequest {
    actionId?: string;
    actionName?: string;
}

export const useQuickActionHalfCard = () => {
    const makeApiCall = useApiContextFetch();
    const { tableId = "", restaurantId = "", parentOrderId = "" } = useContext(TableContext);
    const [showHalfCard, setShowHalfCard] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // To store the previous request in case of error retry
    const previousRequest = useRef<QuickActionHalfRequest | null>(null);

    const quickAction = useCallback(
        async (payBillRequest: QuickActionHalfRequest) => {
            try {
                if (!makeApiCall || isEmpty(payBillRequest?.actionId) || isEmpty(payBillRequest?.actionName)) {
                    throw new Error("Pay bill request is invalid");
                }

                const requestParams = {
                    tableId,
                    restaurantId,
                    actionId: payBillRequest?.actionId,
                    actionName: payBillRequest?.actionName,
                    ...(parentOrderId && { parentOrderId }),
                };

                const response = await triggerQuickAction(requestParams, makeApiCall);

                if (response?.statusCode !== SUCCESS_STATUS_CODE) {
                    throw new Error("Pay bill request failed");
                }
            } catch (error) {
                throw error;
            }
        },
        [makeApiCall, tableId, restaurantId, parentOrderId],
    );

    const handleQuickActionClick = useCallback(
        async (request: QuickActionHalfRequest) => {
            try {
                setIsError(false);
                setIsLoading(true);
                previousRequest.current = request;
                await quickAction(request);
            } catch (error) {
                setIsError(true);
            } finally {
                setIsLoading(false);
                setShowHalfCard(true);
            }
        },
        [quickAction],
    );

    const onHalfCardClose = useCallback(() => {
        setShowHalfCard(false);
    }, []);

    const onHalfCardPrimaryClick = useCallback(
        (shouldRetryOnError = true) => {
            onHalfCardClose();

            if (shouldRetryOnError && isError && previousRequest.current) {
                handleQuickActionClick(previousRequest.current);
            }
        },
        [onHalfCardClose, handleQuickActionClick, isError],
    );

    return {
        showHalfCard,
        isError,
        handleQuickActionClick,
        isLoading,
        onHalfCardClose,
        onHalfCardPrimaryClick,
    };
};
